import { AlpService, LinkService, IFixedformAttempt, ServiceStatusEnum } from "@imaginelearning/mps-api-service";
import { useCallback, useState } from "react";
import useUser from "../useuser";
import { logoutUser } from "../../helpers/user-session";
import { ILinkPlayerProps } from "../../components/project-link-player/project-link-player";
import useAssessmentAttemptOptions, { IAttemptOptionOverrides } from "./useAssessmentAttemptOptions";

interface IFormativeAlp {
  state: string;
  assignmentID: string;
}

/**
 * Custom hook for managing the assessment link player state for the federated module, including the reason processing logic.
 * The createAttempt method must be called before the readyForLaunch and launchProps properties are set.
 * @returns An object containing the following properties:
 *   - createAttempt: A function for creating an assessment attempt.
 *   - readyToLaunch: A boolean indicating whether the player is ready to launch.
 *   - launchProps: An object containing the launch properties to be passed to the ProjectLinkPlayer instance (includes an onClose handler)
 */
export default function useFormativeLinkPlayer(reloadAlp: () => void, onError: () => void, alp: IFormativeAlp) {
  const [readyToLaunch, setReadyToLaunch] = useState(false);
  const [launchProps, setLaunchProps] = useState<ILinkPlayerProps | undefined>(undefined);
  const { userId, user } = useUser();
  const { buildAssessmentAttemptOptions } = useAssessmentAttemptOptions(alp.state, alp.assignmentID);

  const createAttempt = useCallback(
    async (pathId: number, overrides: IAttemptOptionOverrides = {}) => {
      const launchProps: ILinkPlayerProps = {
        pathId: `${pathId}`,
        pathVersion: 0,
        studentId: userId,
        attemptId: ""
      };
      const { response } = await AlpService.assessmentCreateTake(alp.assignmentID, pathId);

      let responseTakeId = "";
      if (typeof response !== "string") {
        const take = response;
        responseTakeId = take.takeID;

        await AlpService.assessmentUpdateTake(take.takeID, "InProgress");

        launchProps.pathId = `${take.pathID}`;
        launchProps.pathVersion = take.version;
        launchProps.attemptId = take.takeID;

        const attemptOptions = buildAssessmentAttemptOptions(take, overrides);

        const res = await LinkService.createAttempt(userId, attemptOptions);

        if (res.status === ServiceStatusEnum.LOADED) {
          launchProps.attemptId = (res.response as IFixedformAttempt)?.attemptId;
        }
      }

      launchProps.onLinkPlayerClose = async (reasonCode: string) => {
        await closeLinkPlayer(reasonCode, responseTakeId);
      };
      launchProps.onLinkPlayerError = (errorMessage: string) => {
        console.error("Error encountered in the Link player", errorMessage);
        onError();
      };
      setLaunchProps(launchProps);
      setReadyToLaunch(true);

      async function closeLinkPlayer(reasonCode: string, takeId: string) {
        setReadyToLaunch(false);
        setLaunchProps(undefined);

        switch (reasonCode) {
          case "SaveAndExit":
          case "Exit":
          case "ReviewExit":
            await reloadAlp();
            return;
          case "CompleteAndExit":
            await AlpService.assessmentUpdateTake(takeId, "Completed");
            await reloadAlp();
            return;
          case "CompleteAndLogout":
          case "CompleteAndTimeout":
            await AlpService.assessmentUpdateTake(takeId, "Completed");
            logoutUser(user);
            return;
          case "Logout":
          case "Timeout":
            logoutUser(user);
            return;
          default: // includes "Unauthorized" and any unaccounted for statuses
            onError();
        }
      }
    },
    [alp, userId, buildAssessmentAttemptOptions, onError, reloadAlp, user]
  );

  return { createAttempt, readyToLaunch, launchProps };
}
